import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';
import { resetUser } from './loginUser';

// GET - Fetch KB settings for a business
export const fetchKbSettings = createAsyncThunk(
    'kbSettings/fetchKbSettings',
    async ({ business_id, group_id }, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            if (!business_id) {
                throw new Error('Business ID not found');
            }
            if (!group_id) {
                throw new Error('Group ID not found');
            }

            const url = `${ROOT_URL}/kb/settings/${business_id}`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                },
                params: { group_id }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching KB settings:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to fetch KB settings'
            );
        }
    }
);

// POST - Save KB settings
export const saveKbSettings = createAsyncThunk(
    'kbSettings/saveKbSettings',
    async (settingsData, { getState, rejectWithValue }) => {
        try {
            const { loginToken } = getState();
            const authToken = loginToken?.response?.token;

            const url = `${ROOT_URL}/kb/settings`;

            const response = await axios.post(url, settingsData, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error saving KB settings:', error);
            return rejectWithValue(
                error.response?.data?.message || 'Failed to save KB settings'
            );
        }
    }
);

const initialState = {
    loading: true,
    saving: false,
    error: null,
    saveError: null,
    settings: null,
    hasFetched: false,
    lastFetchedGroupId: null,
};

const kbSettingsSlice = createSlice({
    name: 'kbSettings',
    initialState,
    reducers: {
        resetKbSettings: (state) => {
            return initialState;
        },
        clearKbSettingsError: (state) => {
            state.error = null;
            state.saveError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch settings
            .addCase(fetchKbSettings.pending, (state, action) => {
                const groupId = action.meta?.arg?.group_id ?? null;
                const sameGroup =
                    state.lastFetchedGroupId != null &&
                    String(state.lastFetchedGroupId) === String(groupId);
                state.loading = !(state.hasFetched && sameGroup);
                if (!sameGroup && state.lastFetchedGroupId != null) {
                    state.settings = null;
                    state.hasFetched = false;
                }
                state.error = null;
            })
            .addCase(fetchKbSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.settings = action.payload?.data || action.payload;
                state.hasFetched = true;
                state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;
            })
            .addCase(fetchKbSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.settings = null;
                state.hasFetched = true;
                state.lastFetchedGroupId = action.meta?.arg?.group_id ?? null;
            })
            // Save settings
            .addCase(saveKbSettings.pending, (state) => {
                state.saving = true;
                state.saveError = null;
            })
            .addCase(saveKbSettings.fulfilled, (state, action) => {
                state.saving = false;
                state.saveError = null;
                state.settings = action.payload?.data || action.payload;
            })
            .addCase(saveKbSettings.rejected, (state, action) => {
                state.saving = false;
                state.saveError = action.payload;
            })

            .addCase(resetUser, () => {
                return initialState;
            });
    }
});

export const { resetKbSettings, clearKbSettingsError } = kbSettingsSlice.actions;

export default kbSettingsSlice.reducer;
